export const PRIVACY_POLICY = {
  slug: "privacy-policy",
  title: "Privacy Policy",
  role_label: "Privacy Policy",
  section_label: "Section",
  company: "NContro",
  short_company: "NContro",
  effective_date: "August 21, 2026",
  version: "2.0",
  intro: [
    'Welcome to NContro ("Company," "Platform," "we," "our," or "us"). Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, store, and protect your information when you access or use our website, mobile applications, and related services (collectively, the "Platform").',
    "By using the Platform, you acknowledge that you have read and understand this Privacy Policy.",
  ],
  articles: [
    {
      n: 1,
      title: "Information We Collect",
      body: [
        { p: "We may collect the following categories of information:" },

        { h: "Information You Provide" },
        { p: "When you create an account, request services, or communicate with us, we may collect:" },
        {
          list: [
            "Name",
            "Business name",
            "Email address",
            "Telephone number",
            "Mailing address",
            "Event details",
            "Messages sent through the Platform",
            "Profile photographs",
            "Menus and service descriptions",
            "Business licenses and insurance documents (for Vendors)",
            "Tax information where required",
            "Customer reviews and ratings",
          ],
        },

        { h: "Payment Information" },
        { p: "Payments are processed by independent third-party payment processors." },
        { p: "The Company does not store full payment card numbers." },
        { p: "We may receive limited payment information such as:" },
        {
          list: [
            "Payment status",
            "Transaction identifiers",
            "Last four digits of payment methods",
            "Billing information provided by the payment processor",
          ],
        },

        { h: "Automatically Collected Information" },
        { p: "When you use the Platform, we may automatically collect:" },
        {
          list: [
            "IP address",
            "Browser type",
            "Device type",
            "Operating system",
            "Referring websites",
            "Pages viewed",
            "Session duration",
            "Search activity",
            "Booking activity",
            "Error logs",
            "Cookies and similar technologies",
          ],
        },

        { h: "Location Information" },
        { p: "With your permission, we may collect approximate or precise location information to improve search results, recommend nearby Vendors, or provide location-based features." },
        { p: "You may disable location permissions through your device settings." },
      ],
    },
    {
      n: 2,
      title: "How We Use Your Information",
      body: [
        { p: "We may use your information to:" },
        {
          list: [
            "Create and manage accounts;",
            "Process bookings;",
            "Facilitate communications between Customers and Vendors;",
            "Process payments;",
            "Verify Vendor identities;",
            "Detect fraud;",
            "Prevent unauthorized activity;",
            "Improve Platform performance;",
            "Personalize search results;",
            "Respond to customer support requests;",
            "Send transactional communications;",
            "Send marketing communications where permitted by law;",
            "Comply with legal obligations;",
            "Enforce our agreements; and",
            "Protect the safety and security of the Platform.",
          ],
        },
      ],
    },
    {
      n: 3,
      title: "Sharing of Information",
      body: [
        { p: "We may share information with:" },

        { h: "Vendors" },
        { p: "Customer information necessary to complete a Booking." },

        { h: "Customers" },
        { p: "Vendor profile information necessary to evaluate and complete a Booking." },

        { h: "Service Providers" },
        { p: "Third parties that assist us with:" },
        {
          list: [
            "Payment processing",
            "Cloud hosting",
            "Customer support",
            "Analytics",
            "Email delivery",
            "SMS messaging",
            "Fraud prevention",
            "Identity verification",
            "Marketing services",
          ],
        },

        { h: "Legal Authorities" },
        { p: "We may disclose information when required to:" },
        {
          list: [
            "Comply with legal obligations;",
            "Respond to subpoenas, court orders, or lawful requests;",
            "Protect the rights, property, or safety of the Company, our users, or others;",
            "Investigate suspected fraud or illegal activity.",
          ],
        },
      ],
    },
    {
      n: 4,
      title: "Cookies and Tracking Technologies",
      body: [
        { p: "The Platform uses cookies and similar technologies to:" },
        {
          list: [
            "Remember user preferences;",
            "Keep users signed in;",
            "Measure traffic;",
            "Improve performance;",
            "Analyze usage;",
            "Deliver relevant content.",
          ],
        },
        { p: "Users may disable cookies through their browser settings, although some features may not function properly." },
      ],
    },
    {
      n: 5,
      title: "Data Retention",
      body: [
        { p: "We retain personal information only for as long as reasonably necessary to:" },
        {
          list: [
            "Provide the Platform;",
            "Complete bookings;",
            "Resolve disputes;",
            "Comply with legal obligations;",
            "Enforce our agreements;",
            "Maintain business records.",
          ],
        },
        { p: "When information is no longer required, it will be securely deleted or anonymized where reasonably practicable." },
      ],
    },
    {
      n: 6,
      title: "Data Security",
      body: [
        { p: "We use commercially reasonable administrative, technical, and physical safeguards designed to protect personal information." },
        { p: "However, no method of electronic transmission or storage is completely secure, and we cannot guarantee absolute security." },
      ],
    },
    {
      n: 7,
      title: "Your Privacy Rights",
      body: [
        { p: "Depending on your location and applicable law, you may have the right to:" },
        {
          list: [
            "Access your personal information;",
            "Correct inaccurate information;",
            "Delete certain personal information;",
            "Obtain a copy of your information where applicable;",
            "Object to certain processing;",
            "Restrict certain processing;",
            "Withdraw consent where processing is based on consent;",
            "Opt out of marketing communications.",
          ],
        },
        { p: "We will respond to applicable requests in accordance with governing law." },
      ],
    },
    {
      n: 8,
      title: "Marketing Communications",
      body: [
        { p: "Users may unsubscribe from promotional emails by using the unsubscribe link contained within those emails." },
        { p: "Transactional messages relating to bookings, payments, account security, or legal notices may continue to be sent even if marketing communications are declined." },
      ],
    },
    {
      n: 9,
      title: "Children's Privacy",
      body: [
        { p: "The Platform is not intended for individuals under the age of 18." },
        { p: "We do not knowingly collect personal information from children. If we learn that personal information has been collected from a child in violation of applicable law, we will take reasonable steps to delete such information." },
      ],
    },
    {
      n: 10,
      title: "Third-Party Services",
      body: [
        { p: "The Platform may contain links to third-party websites or integrate with third-party services." },
        { p: "The Company is not responsible for the privacy practices, security, or content of those third parties. Users should review the privacy policies of any third-party services they use." },
      ],
    },
    {
      n: 11,
      title: "Business Transfers",
      body: [
        { p: "If the Company is involved in a merger, acquisition, financing, restructuring, sale of assets, bankruptcy, or other corporate transaction, user information may be transferred as part of that transaction, subject to applicable law." },
      ],
    },
    {
      n: 12,
      title: "Changes to This Privacy Policy",
      body: [
        { p: "We may modify this Privacy Policy from time to time." },
        { p: "When material changes are made, we will update the Effective Date and provide notice where required by applicable law." },
        { p: "Continued use of the Platform after the updated Privacy Policy becomes effective constitutes acceptance of the revised Privacy Policy." },
      ],
    },
    {
      n: 13,
      title: "Vendor Verification Data",
      body: [
        { p: "When Vendors participate in the Platform's verification program, NContro may collect and process information such as business licenses, insurance certificates, permits, professional certifications, portfolio samples, driver's licenses and vehicle registrations for transportation vendors, related expiration dates, verification status, and other supporting verification documentation." },
        { p: "Authorized NContro personnel and service providers may process this information for verification, fraud prevention, compliance, and Platform safety. Access is restricted to individuals with a legitimate business need." },
        { p: "Vendors are responsible for keeping submitted verification information accurate and for promptly updating information upon material change, expiration, cancellation, suspension, or loss of relevant credentials." },
      ],
    },
    {
      n: 14,
      title: "Third-Party Integrations",
      body: [
        { p: "If a user elects to connect a supported third-party integration to their NContro account, NContro may exchange information reasonably necessary to provide that integration. We follow data-minimization principles and only exchange the fields required for the integration to function." },
        { p: "For calendar-availability integrations (for example, Tripleseat), NContro imports only the information required to determine whether a Vendor is available on a particular date and time period — such as event date, times, status, and internal event identifiers. NContro does not import Tripleseat customer emails, phone numbers, private notes, payment details, or guest personal information for availability purposes." },
        { p: "Users may disconnect an integration at any time. When an integration is disconnected, NContro deactivates further data exchange with that provider. Historical event records already imported may remain in the account's audit history and are subject to the retention terms below." },
      ],
    },
    {
      n: 15,
      title: "Automated and AI-Supported Processing",
      body: [
        { p: "NContro uses automated and AI-supported functionality to power certain Platform features — for example, parsing menu or service documents that Vendors upload, extracting images from Vendor-supplied websites, and similar convenience features." },
        { p: "Information and files submitted for such features may be processed using automated systems and third-party service providers as reasonably necessary to provide those features. NContro selects providers that offer appropriate confidentiality and security commitments." },
        { p: "Automated systems used by NContro do not make legally significant decisions about Users. Verification, moderation, refund review, and account suspension decisions are reviewed by NContro personnel." },
      ],
    },
    {
      n: 16,
      title: "Records for Disputes and Investigations",
      body: [
        { p: "NContro may preserve, review, compile, and where reasonably necessary provide relevant Platform records when responding to refunds, payment disputes, chargebacks, cancellations, no-shows, fraud investigations, safety complaints, or compliance investigations." },
        { p: "Relevant records may include the accepted Proposal or Booking record, the applicable cancellation and refund policy, timestamps, payment and refund records, Platform communications, cancellation and no-show records, and uploaded supporting documentation." },
        { p: "NContro may share relevant information with payment processors, financial institutions, regulators, law enforcement, and other appropriate entities where legally permitted and reasonably necessary." },
      ],
    },
    {
      n: 17,
      title: "Data Retention",
      body: [
        { p: "Deletion of a User account does not require immediate deletion of every record. NContro may retain information reasonably necessary for legal obligations, tax and accounting obligations, Booking records, fraud prevention, security, refunds, disputes, chargebacks, enforcement, and contractual obligations, subject to applicable law." },
        { p: "Retention periods vary by record type and applicable law. Where information is retained beyond active account use, access is limited to individuals with a legitimate business need." },
      ],
    },
    {
      n: 18,
      title: "Contact Us",
      body: [
        { p: "If you have questions regarding this Privacy Policy or our privacy practices, please contact:" },
        {
          contact: {
            company: "NContro",
            address: "2000 N Beauregard St #345, Alexandria, VA 22311",
            email: "scottye@ncontro.com",
            phone: "(901) 573-7661",
            website: "https://www.ncontro.com",
          },
        },
      ],
    },
  ],
};
